import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Search, X, Upload, FileSpreadsheet, Zap, Download, Printer, FileText } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import * as XLSX from 'xlsx';
import { useIsAdmin } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Coerce a user-entered GST % into a number. Preserves explicit 0%; falls back to 5% ONLY
// when the value is truly blank / non-numeric. Preview totals and the save payload MUST
// share this helper so what the user sees is exactly what gets stored & printed.
const coerceGstPercent = (raw, fallback = 5) => {
  if (raw === null || raw === undefined) return fallback;
  const s = String(raw).trim();
  if (s === '') return fallback;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : fallback;
};

const EMPTY_ITEM = () => ({ _key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: '', brand: '', hsn_code: '', batch_number: '', quantity: '', rate: '', gst_percentage: 5 });

const QuickInvoice = () => {
  const isAdmin = useIsAdmin();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [defaultCustomerId, setDefaultCustomerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    items: [EMPTY_ITEM()],
    notes: '',
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [invRes, custRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/quick-invoices`),
        axios.get(`${API_URL}/customers`),
        axios.get(`${API_URL}/settings/default-customer`).catch(() => ({ data: null })),
      ]);
      setInvoices(invRes.data || []);
      setCustomers(custRes.data || []);
      const defId = settingsRes.data?.customer_id || '';
      setDefaultCustomerId(defId);
    } catch (e) {
      toast.error('Failed to load quick invoices');
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setFormData({
      customer_id: defaultCustomerId || '',
      invoice_date: new Date().toISOString().split('T')[0],
      items: [EMPTY_ITEM()],
      notes: '',
    });
    setIsDialogOpen(true);
  };

  const addRow = () => {
    setFormData(prev => ({ ...prev, items: [...prev.items, EMPTY_ITEM()] }));
  };

  const removeRow = (idx) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items,
    }));
  };

  const updateItem = (idx, field, value) => {
    setFormData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  // Download an Excel template with the exact columns Quick GST Invoice expects.
  // Users can fill this and re-upload via Import Excel — zero guesswork on column names.
  const downloadTemplate = () => {
    const rows = [
      {
        'Description': 'Dental Implant',
        'Brand': 'Refirm',
        'HSN Code': '90211000',
        'Batch Number': 'REF-B-2401',
        'Quantity': 5,
        'Rate': 1500,
        'GST %': 5,
      },
      {
        'Description': 'Cover Screw',
        'Brand': 'Osstem',
        'HSN Code': '90211000',
        'Batch Number': 'OST-CS-2404',
        'Quantity': 20,
        'Rate': 80,
        'GST %': 12,
      },
    ];
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ['Description', 'Brand', 'HSN Code', 'Batch Number', 'Quantity', 'Rate', 'GST %'],
    });
    // Give each column a comfortable width
    ws['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Quick GST Invoice');
    XLSX.writeFile(wb, 'quick_gst_invoice_template.xlsx');
    toast.success('Template downloaded — fill in your items and re-upload via Import Excel');
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        if (!rows.length) {
          toast.error('Excel is empty');
          return;
        }
        // Map columns flexibly
        const pickGst = (row) => {
          const raw = row['GST %'] ?? row['GST'] ?? row['gst_percentage'] ?? row['gst'];
          return coerceGstPercent(raw);
        };
        const items = rows.map(r => ({
          _key: `xl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          description: String(r['Description'] || r['description'] || r['Product'] || '').trim(),
          brand: String(r['Brand'] || r['brand'] || r['Manufacturer'] || '').trim(),
          hsn_code: String(r['HSN Code'] || r['HSN'] || r['hsn_code'] || r['hsn'] || '').trim(),
          batch_number: String(r['Batch Number'] || r['Batch'] || r['batch_number'] || r['batch'] || '').trim(),
          quantity: parseFloat(r['Quantity'] || r['Qty'] || r['quantity'] || 0) || '',
          rate: parseFloat(r['Rate'] || r['Price'] || r['rate'] || 0) || '',
          gst_percentage: pickGst(r),
        })).filter(it => it.description);
        if (items.length === 0) {
          toast.error('No valid rows found. Expected columns: Description, Brand, HSN Code, Batch Number, Quantity, Rate, GST %');
          return;
        }
        setFormData(prev => ({ ...prev, items }));
        toast.success(`Loaded ${items.length} item(s) from Excel`);
      } catch (err) {
        toast.error('Failed to parse Excel');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // Live totals for preview (same math as backend so preview matches saved invoice)
  const selectedCustomer = customers.find(c => c.id === formData.customer_id);
  // Interstate detection uses GSTIN state code prefix (33 = Tamil Nadu = company state).
  // Falls back to state field comparison when GSTIN is missing.
  const isInterstate = useMemo(() => {
    if (!selectedCustomer) return false;
    const gstin = String(selectedCustomer.gstin || '').trim();
    if (gstin.length >= 2) return gstin.slice(0, 2) !== '33';
    const cs = String(selectedCustomer.state || '').trim().toLowerCase();
    return cs !== '' && cs !== 'tamil nadu';
  }, [selectedCustomer]);

  const totals = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0;
    formData.items.forEach(it => {
      const qty = parseFloat(it.quantity) || 0;
      const rate = parseFloat(it.rate) || 0;
      const gst = coerceGstPercent(it.gst_percentage);
      const t = qty * rate;
      taxable += t;
      if (isInterstate) {
        igst += t * gst / 100;
      } else {
        cgst += t * gst / 200;
        sgst += t * gst / 200;
      }
    });
    const subtotal = taxable + cgst + sgst + igst;
    const grand = Math.round(subtotal);
    const roundOff = grand - subtotal;
    return {
      taxable: Number(taxable.toFixed(2)),
      cgst: Number(cgst.toFixed(2)),
      sgst: Number(sgst.toFixed(2)),
      igst: Number(igst.toFixed(2)),
      totalGst: Number((cgst + sgst + igst).toFixed(2)),
      subtotal: Number(subtotal.toFixed(2)),
      roundOff: Number(roundOff.toFixed(2)),
      grand: Number(grand.toFixed(2)),
    };
  }, [formData.items, isInterstate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    const validItems = formData.items.filter(it => it.description && Number(it.quantity) > 0 && Number(it.rate) >= 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid item (description, quantity, rate)');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        invoice_date: formData.invoice_date,
        notes: formData.notes || '',
        items: validItems.map(it => ({
          description: it.description,
          brand: it.brand || '',
          hsn_code: it.hsn_code || '',
          batch_number: it.batch_number || '',
          quantity: parseFloat(it.quantity),
          rate: parseFloat(it.rate),
          gst_percentage: coerceGstPercent(it.gst_percentage),
        })),
      };
      const res = await axios.post(`${API_URL}/quick-invoices`, payload);
      toast.success(`Quick GST Invoice ${res.data.invoice_number} saved`);
      setIsDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auth-safe blob PDF open (either download or print)
  const openInvoicePdf = async (invId, mode = 'download') => {
    try {
      const res = await axios.get(`${API_URL}/quick-invoices/${invId}/download-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (mode === 'print') {
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (win) {
          win.addEventListener('load', () => {
            try {
              win.print();
            } catch (printErr) {
              // Browser blocked programmatic print or window was closed early — user can still Ctrl+P
              if (process.env.NODE_ENV === 'development') {
                console.warn('Auto-print failed for quick invoice PDF:', printErr);
              }
            }
          });
        } else {
          toast.info('Popup blocked. Please allow popups to print.');
        }
      } else {
        const a = document.createElement('a');
        a.href = url;
        const inv = invoices.find(x => x.id === invId);
        a.download = `${(inv?.invoice_number || 'quick-invoice').replace(/\//g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Failed to fetch PDF');
    }
  };

  const handleDelete = async (invId) => {
    if (!window.confirm('Delete this Quick GST Invoice? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/quick-invoices/${invId}`);
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filteredInvoices = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return invoices;
    return invoices.filter(inv =>
      inv.invoice_number.toLowerCase().includes(t) ||
      (inv.customer_name || '').toLowerCase().includes(t)
    );
  }, [invoices, searchTerm]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-teal-600" /> Quick GST Invoice
          </h1>
          <p className="text-sm text-slate-500">Standalone GST tax invoices — no inventory or product master updates.</p>
        </div>
        <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={openNewDialog} data-testid="new-quick-invoice-btn">
          <Plus className="w-4 h-4 mr-1" /> New Quick GST Invoice
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quick GST Invoice Register</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by invoice # or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="quick-invoice-search"
            />
          </div>

          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading…</p>
          ) : filteredInvoices.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No quick invoices yet. Click <b>New Quick GST Invoice</b> to create one.</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Invoice #</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Customer</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map(inv => (
                    <TableRow key={inv.id} data-testid={`quick-invoice-row-${inv.id}`}>
                      <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                      <TableCell className="text-sm">{formatDate(inv.invoice_date)}</TableCell>
                      <TableCell className="text-sm">{inv.customer_name}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatCurrency(inv.grand_total)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize bg-teal-50 text-teal-700 border-teal-200">
                          {inv.status || 'issued'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openInvoicePdf(inv.id, 'print')}
                            title="Print"
                            data-testid={`quick-print-${inv.id}`}
                          >
                            <Printer className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openInvoicePdf(inv.id, 'download')}
                            title="Download PDF"
                            data-testid={`quick-download-${inv.id}`}
                          >
                            <Download className="w-4 h-4 text-slate-600" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(inv.id)}
                              title="Delete"
                              data-testid={`quick-delete-${inv.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Quick GST Invoice Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="quick-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-teal-600" /> New Quick GST Invoice
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Customer + Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(v) => setFormData({ ...formData, customer_id: v })}
                >
                  <SelectTrigger data-testid="quick-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.gstin ? `— ${c.gstin}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isInterstate && (
                  <p className="text-[11px] text-amber-700">Interstate — IGST will apply.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Invoice Date *</Label>
                <Input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  required
                  data-testid="quick-invoice-date-input"
                />
              </div>
            </div>

            {/* Line Items header + actions */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">
                {formData.items.filter(i => i.description).length} item(s)
              </span>
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleExcelUpload}
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="quick-excel-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  data-testid="quick-template-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> Template
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="quick-import-excel-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Import Excel
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="quick-add-row-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Row
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Description *</TableHead>
                    <TableHead className="text-xs" style={{ width: '130px' }}>Brand</TableHead>
                    <TableHead className="text-xs" style={{ width: '100px' }}>HSN Code</TableHead>
                    <TableHead className="text-xs" style={{ width: '120px' }}>Batch Number</TableHead>
                    <TableHead className="text-xs text-center" style={{ width: '75px' }}>Qty *</TableHead>
                    <TableHead className="text-xs text-center" style={{ width: '100px' }}>Rate (₹) *</TableHead>
                    <TableHead className="text-xs text-center" style={{ width: '75px' }}>GST %</TableHead>
                    <TableHead className="text-xs text-right" style={{ width: '110px' }}>Line Total</TableHead>
                    <TableHead style={{ width: '40px' }}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((it, idx) => {
                    const qty = parseFloat(it.quantity) || 0;
                    const rate = parseFloat(it.rate) || 0;
                    const gst = coerceGstPercent(it.gst_percentage);
                    const taxable = qty * rate;
                    const gstAmt = taxable * gst / 100;
                    const lineTotal = taxable + gstAmt;
                    return (
                      <TableRow key={it._key || `row-${idx}`}>
                        <TableCell className="p-2">
                          <Input
                            value={it.description}
                            onChange={(e) => updateItem(idx, 'description', e.target.value)}
                            placeholder="Item description"
                            className="h-9 text-sm"
                            data-testid={`quick-item-desc-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={it.brand || ''}
                            onChange={(e) => updateItem(idx, 'brand', e.target.value)}
                            placeholder="e.g. Refirm"
                            className="h-9 text-sm"
                            data-testid={`quick-item-brand-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={it.hsn_code}
                            onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)}
                            placeholder="e.g. 90211000"
                            className="h-9 text-sm"
                            data-testid={`quick-item-hsn-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={it.batch_number}
                            onChange={(e) => updateItem(idx, 'batch_number', e.target.value)}
                            placeholder="Optional"
                            className="h-9 text-sm"
                            data-testid={`quick-item-batch-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={it.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                            className="h-9 text-sm text-center"
                            data-testid={`quick-item-qty-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={it.rate}
                            onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                            className="h-9 text-sm text-center"
                            data-testid={`quick-item-rate-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={it.gst_percentage}
                            onChange={(e) => updateItem(idx, 'gst_percentage', e.target.value)}
                            className="h-9 text-sm text-center"
                            data-testid={`quick-item-gst-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="p-2 text-right tabular-nums">
                          {lineTotal > 0 ? formatCurrency(lineTotal) : '-'}
                        </TableCell>
                        <TableCell className="p-2">
                          {formData.items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRow(idx)}
                              className="h-8 w-8 p-0"
                              data-testid={`quick-remove-row-${idx}`}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Totals preview */}
            {totals.grand > 0 && (
              <div className="bg-slate-50 border rounded-lg p-4">
                <div className="flex justify-end">
                  <div className="w-80 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Taxable Value:</span>
                      <span className="tabular-nums font-medium">{formatCurrency(totals.taxable)}</span>
                    </div>
                    {isInterstate ? (
                      <div className="flex justify-between">
                        <span className="text-slate-600">IGST:</span>
                        <span className="tabular-nums font-medium">{formatCurrency(totals.igst)}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-600">CGST:</span>
                          <span className="tabular-nums font-medium">{formatCurrency(totals.cgst)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">SGST:</span>
                          <span className="tabular-nums font-medium">{formatCurrency(totals.sgst)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total GST:</span>
                      <span className="tabular-nums font-medium">{formatCurrency(totals.totalGst)}</span>
                    </div>
                    {totals.roundOff !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Round Off:</span>
                        <span className="tabular-nums font-medium">{formatCurrency(totals.roundOff)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t text-base">
                      <span className="font-semibold">Grand Total:</span>
                      <span className="tabular-nums font-bold text-teal-600" data-testid="quick-grand-total">
                        {formatCurrency(totals.grand)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-teal-600 hover:bg-teal-700 text-white"
                disabled={isSubmitting}
                data-testid="save-quick-invoice-btn"
              >
                <FileText className="w-4 h-4 mr-1" />
                {isSubmitting ? 'Saving…' : 'Save Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickInvoice;
